/**
 * Samtykke (AP11, spec 09 §C / 14-maling-og-samtykke.md).
 *
 * «Basic» Consent Mode v2: ingen Google-/Meta-script finnes i HTML-en.
 * Denne modulen leser cookien `skjld_consent` (JSON: version, ts, analytics,
 * marketing), driver banneret (<x-consent-banner>), og injiserer gtag.js /
 * fbevents.js FØRST når besøkende har samtykket i respektive kategori.
 *
 *   window.skjld.consent.get()            → {version, ts, analytics, marketing} | null
 *   window.skjld.consent.set({analytics, marketing})
 *   window.skjld.consent.open()           → åpner banneret (footer-lenken bruker [data-consent-open])
 *   window.skjld.consent.onChange(fn)
 *
 * Alt som leses fra window.skjld.config skrives av <x-analytics> (ID-er,
 * endepunkt) og <x-meta-pixel> (window.skjld.meta, kun på /book-flyten).
 * GTM og Clarity lastes aldri.
 */

const CATEGORIES = ['analytics', 'marketing'];

const state = {
    listeners: [],
    gaLoaded: false,
    metaLoaded: false,
    lastFocus: null,
    trapHandler: null,
};

function config() {
    return (window.skjld && window.skjld.config) || {};
}

function consentConfig() {
    const c = config().consent || {};
    return {
        cookie: c.cookie || 'skjld_consent',
        version: parseInt(c.version, 10) || 1,
        days: parseInt(c.days, 10) || 180,
    };
}

// ---------------------------------------------------------------------------
// Lagring
// ---------------------------------------------------------------------------

function readCookie(name) {
    const prefix = name + '=';
    const parts = document.cookie ? document.cookie.split('; ') : [];
    for (const part of parts) {
        if (part.startsWith(prefix)) {
            try {
                return decodeURIComponent(part.slice(prefix.length));
            } catch (e) {
                return part.slice(prefix.length);
            }
        }
    }
    return null;
}

function parse(raw) {
    if (!raw) return null;
    try {
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (!data || typeof data !== 'object') return null;
        const version = parseInt(data.version, 10);
        if (!version || version < consentConfig().version) return null;
        const ts = typeof data.ts === 'string' ? data.ts : null;
        if (ts && Date.now() - Date.parse(ts) > consentConfig().days * 86400000) return null;
        return {
            version,
            ts,
            analytics: data.analytics === true,
            marketing: data.marketing === true,
        };
    } catch (e) {
        return null;
    }
}

function getConsent() {
    const { cookie } = consentConfig();
    let current = parse(readCookie(cookie));

    if (!current) {
        // Safari (ITP) kaster JS-satte cookies etter 7 dager; localStorage er
        // reserven. Gyldig kopi der → gjenopprett cookien.
        try {
            const stored = parse(localStorage.getItem(cookie));
            if (stored) {
                writeCookie(stored);
                current = stored;
            }
        } catch (e) { /* ingen localStorage */ }
    }

    return current;
}

function writeCookie(data) {
    const { cookie, days } = consentConfig();
    const value = JSON.stringify(data);
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = cookie + '=' + encodeURIComponent(value) + '; path=/; max-age=' + (days * 86400) + '; SameSite=Lax' + secure;
    try { localStorage.setItem(cookie, value); } catch (e) { /* ignorer */ }
}

function expireCookie(name) {
    const host = window.location.hostname;
    const domains = ['', host, '.' + host, '.' + host.split('.').slice(-2).join('.')];
    for (const domain of domains) {
        document.cookie = name + '=; path=/; max-age=0' + (domain ? '; domain=' + domain : '');
    }
}

// ---------------------------------------------------------------------------
// Tredjeparts-tagger – kun etter samtykke
// ---------------------------------------------------------------------------

function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
}

function updateConsentMode(consent) {
    const analytics = consent && consent.analytics ? 'granted' : 'denied';
    const marketing = consent && consent.marketing ? 'granted' : 'denied';
    gtag('consent', 'update', {
        analytics_storage: analytics,
        ad_storage: marketing,
        ad_user_data: marketing,
        // Annonsepersonalisering er alltid av (09 §C).
        ad_personalization: 'denied',
    });
}

function loadGa4() {
    const id = config().ga4;
    if (!id || state.gaLoaded) return;
    state.gaLoaded = true;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id);
    document.head.appendChild(script);

    window.gtag = window.gtag || gtag;
    gtag('js', new Date());
    gtag('config', id, {
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        cookie_flags: 'SameSite=Lax;Secure',
    });
}

function loadMeta() {
    const meta = window.skjld && window.skjld.meta;
    const pixelId = config().metaPixel || (meta && meta.pixelId);
    if (!pixelId || state.metaLoaded) return;
    state.metaLoaded = true;

    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */

    const userData = meta && meta.userData && Object.keys(meta.userData).length ? meta.userData : undefined;
    window.fbq('consent', 'grant');
    window.fbq('init', pixelId, userData);
    window.fbq('track', 'PageView');
    if (meta && meta.event && meta.eventId) {
        window.fbq('track', meta.event, meta.eventData || {}, { eventID: meta.eventId });
    }
}

function apply(consent) {
    updateConsentMode(consent);

    if (consent && consent.analytics) {
        loadGa4();
    } else if (state.gaLoaded) {
        ['_ga', '_gid', '_gat'].forEach(expireCookie);
        document.cookie.split('; ').forEach((c) => {
            const name = c.split('=')[0];
            if (name.startsWith('_ga_')) expireCookie(name);
        });
    }

    if (consent && consent.marketing) {
        loadMeta();
    } else if (state.metaLoaded && typeof window.fbq === 'function') {
        window.fbq('consent', 'revoke');
        ['_fbp', '_fbc'].forEach(expireCookie);
    }
}

// ---------------------------------------------------------------------------
// Banner
// ---------------------------------------------------------------------------

function banner() {
    return document.querySelector('[data-consent-banner]');
}

function setConsent(choice) {
    const data = {
        version: consentConfig().version,
        ts: new Date().toISOString(),
        analytics: !!choice.analytics,
        marketing: !!choice.marketing,
    };
    writeCookie(data);
    apply(data);
    state.listeners.forEach((fn) => { try { fn(data); } catch (e) { /* ignorer */ } });
    if (window.skjld && typeof window.skjld.track === 'function') {
        window.skjld.track('consent_update', { analytics: data.analytics, marketing: data.marketing });
    }
    return data;
}

function showPanel(el, show) {
    const main = el.querySelector('[data-consent-main]');
    const panel = el.querySelector('[data-consent-panel]');
    const toggle = el.querySelector('[data-consent-action="customise"]');
    if (!main || !panel) return;

    main.hidden = show;
    panel.hidden = !show;
    if (toggle) toggle.setAttribute('aria-expanded', show ? 'true' : 'false');

    if (show) {
        // Forhåndsutfyll fra gjeldende samtykke (aldri avkrysset uten valg).
        const current = getConsent();
        CATEGORIES.forEach((key) => {
            const box = panel.querySelector('[data-consent-category="' + key + '"]');
            if (box) box.checked = !!(current && current[key]);
        });
        trapFocus(panel);
        const first = panel.querySelector('input:not([disabled]), button');
        if (first) first.focus();
    } else {
        releaseFocus();
        if (toggle) toggle.focus();
    }
}

function focusable(root) {
    return Array.from(root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
        .filter((node) => !node.hidden && node.offsetParent !== null);
}

function trapFocus(panel) {
    releaseFocus();
    state.trapHandler = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            showPanel(banner(), false);
            return;
        }
        if (event.key !== 'Tab') return;
        const nodes = focusable(panel);
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };
    document.addEventListener('keydown', state.trapHandler);
}

function releaseFocus() {
    if (state.trapHandler) {
        document.removeEventListener('keydown', state.trapHandler);
        state.trapHandler = null;
    }
}

function openBanner() {
    const el = banner();
    if (!el) return;
    state.lastFocus = document.activeElement;
    el.hidden = false;
    el.setAttribute('data-consent-open-state', 'open');
    showPanel(el, false);
    const first = el.querySelector('[data-consent-action="necessary"]');
    if (first) first.focus();
}

function closeBanner() {
    const el = banner();
    if (!el) return;
    releaseFocus();
    el.hidden = true;
    el.setAttribute('data-consent-open-state', 'closed');
    if (state.lastFocus && typeof state.lastFocus.focus === 'function' && document.contains(state.lastFocus)) {
        state.lastFocus.focus();
    }
    state.lastFocus = null;
}

function readPanelChoice(el) {
    const choice = {};
    CATEGORIES.forEach((key) => {
        const box = el.querySelector('[data-consent-category="' + key + '"]');
        choice[key] = !!(box && box.checked);
    });
    return choice;
}

function bindBanner() {
    const el = banner();
    if (!el) return;

    el.addEventListener('click', (event) => {
        const button = event.target.closest('[data-consent-action]');
        if (!button) return;
        const action = button.getAttribute('data-consent-action');

        switch (action) {
            case 'necessary':
                setConsent({ analytics: false, marketing: false });
                closeBanner();
                break;
            case 'all':
                setConsent({ analytics: true, marketing: true });
                closeBanner();
                break;
            case 'customise':
                showPanel(el, true);
                break;
            case 'back':
                showPanel(el, false);
                break;
            case 'save':
                setConsent(readPanelChoice(el));
                closeBanner();
                break;
            default:
                break;
        }
    });

    document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-consent-open]');
        if (!opener) return;
        event.preventDefault();
        openBanner();
    });
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

export function initConsent() {
    window.skjld = window.skjld || {};
    window.skjld.consent = {
        get: getConsent,
        set: setConsent,
        open: openBanner,
        onChange(fn) { if (typeof fn === 'function') state.listeners.push(fn); },
    };

    const current = getConsent();
    const el = banner();

    if (current) {
        if (el) {
            el.hidden = true;
            el.setAttribute('data-consent-open-state', 'closed');
        }
        apply(current);
    } else if (el && el.hidden) {
        // Serveren skjulte banneret ut fra cookien, men den er ugyldig/utløpt
        // for oss (versjon/alder) → vis den igjen.
        el.hidden = false;
        el.setAttribute('data-consent-open-state', 'open');
    }

    bindBanner();
}

export default initConsent;
